import { useState, type FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import { useCompany } from '../../context/CompanyContext';
import type { Company } from '../../types';

export default function CompaniesPage() {
  const {
    companies,
    loading,
    refreshCompanies,
    updateCompany,
  } = useCompany();
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    code: '',
    name: '',
    gst: '',
    address: '',
    phone: '',
    email: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    signatory: '',
  });

  function openAddModal() {
    setEditingId(null);
    setForm({
      code: '',
      name: '',
      gst: '',
      address: '',
      phone: '',
      email: '',
      bank_name: '',
      account_number: '',
      ifsc_code: '',
      signatory: '',
    });
    setShowModal(true);
  }

  function openEditModal(c: Company) {
    setEditingId(c.id || null);
    setForm({
      code: c.code || '',
      name: c.name || '',
      gst: c.gst || '',
      address: c.address || '',
      phone: c.phone || '',
      email: c.email || '',
      bank_name: c.bank_name || '',
      account_number: c.account_number || '',
      ifsc_code: c.ifsc_code || '',
      signatory: c.signatory || '',
    });
    setShowModal(true);
  }

  async function handleSaveCompany(e: FormEvent) {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) {
      setError('Company Code and Legal Name are required.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim().toUpperCase(),
      gst: form.gst.trim() || null,
      address: form.address.trim() || null,
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      bank_name: form.bank_name.trim() || null,
      account_number: form.account_number.trim() || null,
      ifsc_code: form.ifsc_code.trim() || null,
      signatory: form.signatory.trim() || null,
    };

    try {
      if (editingId) {
        const ok = await updateCompany(editingId, payload);
        if (!ok) throw new Error('Could not update company details');
        setSuccessMsg(`Company entity "${payload.name}" updated successfully.`);
      } else {
        const { error: insertErr } = await supabase
          .from('companies')
          .insert([payload]);

        if (insertErr) throw insertErr;
        setSuccessMsg(`New company entity "${payload.name}" created successfully.`);
        await refreshCompanies();
      }

      setShowModal(false);
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to save company details');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteCompany(id: number, name: string) {
    if (!window.confirm(`Are you sure you want to delete company "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error: delErr } = await supabase.from('companies').delete().eq('id', id);
      if (delErr) throw delErr;
      await refreshCompanies();
      setSuccessMsg(`Company "${name}" deleted.`);
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete company');
    }
  }

  const filteredCompanies = companies.filter((c) => {
    const q = searchQuery.toLowerCase();
    return (
      c.code.toLowerCase().includes(q) ||
      c.name.toLowerCase().includes(q) ||
      (c.gst && c.gst.toLowerCase().includes(q)) ||
      (c.address && c.address.toLowerCase().includes(q)) ||
      (c.bank_name && c.bank_name.toLowerCase().includes(q))
    );
  });

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900">
            Operating Companies & Branches
          </h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Manage corporate legal entities, GSTIN numbers, manufacturing plant addresses, and bank accounts
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-blue-600 hover:bg-blue-700 active:scale-95 text-white text-xs sm:text-sm font-bold px-4 py-2.5 rounded-xl shadow-md shadow-blue-600/20 transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
        >
          <span>+</span>
          <span>Add New Entity</span>
        </button>
      </div>

      {/* Success Notification */}
      {successMsg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl text-xs font-bold flex items-center gap-2">
          <span>✓</span>
          <span>{successMsg}</span>
        </div>
      )}

      {/* Error Notification */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-xs font-bold flex items-center gap-2">
          <span>✕</span>
          <span>{error}</span>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-4 sm:p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="w-full sm:w-96 relative">
          <input
            type="text"
            placeholder="Search by code, company name, GST, or bank..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-xs sm:text-sm bg-white border border-slate-300 rounded-xl focus:border-blue-500 shadow-sm text-slate-900 placeholder-slate-400 transition-colors"
          />
          <span className="absolute left-3.5 top-3 text-slate-400 text-sm">🔍</span>
        </div>
        <div className="text-xs font-semibold text-slate-500">
          Showing <strong className="text-slate-900">{filteredCompanies.length}</strong> of{' '}
          <strong className="text-slate-900">{companies.length}</strong> total company entities
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="p-16 text-center text-slate-500 space-y-3 bg-white rounded-2xl border border-slate-200">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto"></div>
          <span className="text-xs font-semibold">Loading company entities...</span>
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="p-16 text-center text-slate-400 space-y-2 bg-white rounded-2xl border border-slate-200">
          <span className="text-3xl">🏢</span>
          <p className="text-sm font-bold text-slate-700">No company entities match your search</p>
          <p className="text-xs text-slate-500">Click &quot;+ Add New Entity&quot; above to register a new branch.</p>
        </div>
      ) : (
        /* Companies Grid Cards */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCompanies.map((c) => (
            <div
              key={c.id}
              className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6 flex flex-col justify-between hover:border-blue-400 hover:shadow-elevated transition-all space-y-4"
            >
              <div className="space-y-3">
                {/* Card Top: Code and Name */}
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <span className="font-mono text-xs font-extrabold px-2.5 py-1 rounded-lg bg-blue-100 text-blue-800 border border-blue-200">
                      {c.code}
                    </span>
                    <h3 className="font-black text-slate-900 text-base mt-2 leading-snug">
                      {c.name}
                    </h3>
                  </div>
                </div>

                {/* GSTIN Badge */}
                {c.gst && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 flex items-center justify-between">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                      GSTIN
                    </span>
                    <span className="font-mono text-xs font-bold text-slate-900">{c.gst}</span>
                  </div>
                )}

                {/* Address */}
                {c.address && (
                  <div className="text-xs text-slate-600 flex items-start gap-2">
                    <span className="text-sm text-slate-400 mt-0.5">📍</span>
                    <span className="leading-relaxed line-clamp-3">{c.address}</span>
                  </div>
                )}

                {/* Contact Phone & Email */}
                {(c.phone || c.email) && (
                  <div className="text-xs text-slate-600 space-y-1 pt-1 border-t border-slate-100">
                    {c.phone && (
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400">📞</span>
                        <span className="font-mono text-[11px] truncate">{c.phone}</span>
                      </div>
                    )}
                    {c.email && (
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400">✉️</span>
                        <span className="text-[11px] text-blue-600 truncate">{c.email}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Bank Details Remittance Card */}
                {(c.bank_name || c.account_number || c.ifsc_code) && (
                  <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100 text-[11px] space-y-1">
                    <div className="font-bold text-blue-950 flex items-center gap-1.5">
                      <span>🏦</span>
                      <span>Remittance Banker</span>
                    </div>
                    {c.bank_name && <p className="text-slate-700">{c.bank_name}</p>}
                    {c.account_number && (
                      <p className="font-mono text-slate-900">
                        <strong className="text-slate-600">A/C:</strong> {c.account_number}
                      </p>
                    )}
                    {c.ifsc_code && (
                      <p className="font-mono text-slate-900">
                        <strong className="text-slate-600">IFSC:</strong> {c.ifsc_code}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons Footer */}
              <div className="pt-3 border-t border-slate-100 flex justify-end gap-2">
                <button
                  onClick={() => openEditModal(c)}
                  className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold rounded-xl text-xs transition-colors cursor-pointer"
                >
                  Edit Details ✏️
                </button>
                <button
                  onClick={() => c.id && handleDeleteCompany(c.id, c.name)}
                  className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 font-bold rounded-xl text-xs transition-colors cursor-pointer"
                >
                  Delete 🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit / Add Company Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 sm:p-7 space-y-5 shadow-2xl border border-slate-200 my-8">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">
                  {editingId ? 'Modify Record' : 'New Entity'}
                </span>
                <h3 className="text-lg font-bold text-slate-900">
                  {editingId ? 'Edit Company Details' : 'Add New Operating Company'}
                </h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600 font-bold text-lg p-1 rounded-lg hover:bg-slate-100"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveCompany} className="space-y-4 text-xs">
              {/* Section 1: Identifiers */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="block font-bold text-slate-700 mb-1">
                    Entity Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="e.g. SKI, TCI"
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono font-bold"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block font-bold text-slate-700 mb-1">
                    Legal Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. SAI KRISHNA INDUSTRIES"
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-bold"
                  />
                </div>
              </div>

              {/* Section 2: GSTIN */}
              <div>
                <label className="block font-bold text-slate-700 mb-1">GSTIN Number</label>
                <input
                  value={form.gst}
                  onChange={(e) => setForm({ ...form, gst: e.target.value.toUpperCase() })}
                  placeholder="03ACQFS9127A1Z6"
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono"
                />
              </div>

              {/* Section 3: Registered Plant Address */}
              <div>
                <label className="block font-bold text-slate-700 mb-1">Plant / Registered Address</label>
                <textarea
                  rows={2}
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="B-XXIX-536/35-B/A, Sua Road, Near Eastman Chowk, Industrial Area-C, Giaspura, Ludhiana – 141010 (Pb.)"
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 leading-relaxed"
                />
              </div>

              {/* Section 4: Contact Info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Phone Number(s)</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="0161-5034059, 93167-53530"
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="sutrantechnoconveyors@gmail.com"
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Section 5: Banking Details */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                <div className="font-bold text-slate-900 flex items-center gap-1.5 text-xs">
                  <span>🏦</span>
                  <span>Bank Remittance Remittance Details</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Bank Name & Branch</label>
                    <input
                      value={form.bank_name}
                      onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                      placeholder="Bank of Baroda, Ludhiana"
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Account Number</label>
                    <input
                      value={form.account_number}
                      onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                      placeholder="78450500000073"
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono bg-white"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">IFSC Code</label>
                    <input
                      value={form.ifsc_code}
                      onChange={(e) => setForm({ ...form, ifsc_code: e.target.value.toUpperCase() })}
                      placeholder="BARB0VJMLUD"
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Section 6: Authorised Signatory */}
              <div>
                <label className="block font-bold text-slate-700 mb-1">Authorised Signatory Name / Title</label>
                <input
                  value={form.signatory}
                  onChange={(e) => setForm({ ...form, signatory: e.target.value })}
                  placeholder="Authorised Signatory / Director"
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                />
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-600/20 disabled:opacity-50 transition-colors cursor-pointer"
                >
                  {saving ? 'Saving...' : editingId ? 'Update Company' : 'Save Entity'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
